import { useCallback, useMemo } from 'react';
import { IconInfoOutline } from '@picsart/design-system/foundation/icons/IconInfoOutline';

import { getContext } from '@/context';
import useStyles from './styles';
import { SettingsPanelVariant, type SettingsPanelProps } from './types';
import { HELP_LINKS, ABOUT_LINKS, formatVersionCode } from './constants';
import SettingsHeader from './SettingsHeader';
import SettingsLinkButton from './SettingsLinkButton';
import SettingsActions from './SettingsActions';

const SettingsPanel = ({
  variant,
  onBack,
  isAuthorized = false,
}: SettingsPanelProps) => {
  const classes = useStyles();
  const isPage = variant === SettingsPanelVariant.Page;

  const openUrl = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const appVersion = useMemo(() => {
    try {
      const versionCode = getContext().utils.api.getHeaders()?.versioncode;
      return formatVersionCode(versionCode);
    } catch {
      return '';
    }
  }, []);

  return (
    <div className={isPage ? classes.page : classes.dropdown}>
      <SettingsHeader variant={variant} onBack={onBack} />

      <div className={classes.content}>
        <div className={classes.section}>
          <span className={classes.sectionTitle}>Help</span>
          <div className={classes.sectionItems}>
            {HELP_LINKS.map((link) => (
              <SettingsLinkButton
                key={link.label}
                link={link}
                onClick={openUrl}
              />
            ))}
          </div>
        </div>

        <div className={classes.section}>
          <span className={classes.sectionTitle}>About</span>
          <div className={classes.sectionItems}>
            {ABOUT_LINKS.map((link) => (
              <SettingsLinkButton
                key={link.label}
                link={link}
                onClick={openUrl}
              />
            ))}
          </div>
        </div>

        {appVersion && (
          <div className={classes.versionRow}>
            <IconInfoOutline
              fill="currentColor"
              className={classes.versionIcon}
            />
            <span className={classes.versionLabel}>Version</span>
            <span className={classes.versionValue}>{appVersion}</span>
          </div>
        )}
      </div>

      <SettingsActions isAuthorized={isAuthorized} />
    </div>
  );
};

export default SettingsPanel;
